/**
 * ScrollPulseHook - Handles scrolling to new elements and applying pulse animations
 * 
 * Usage:
 * - Add phx-hook="ScrollPulseHook" to elements that should be scrolled to and pulsed
 * - Send "scroll-and-pulse" event from LiveView to trigger the animation
 * - The hook will scroll the element into view and apply a pulse animation
 */
const ScrollPulseHook = {
  mounted() {
    // Store timeouts for cleanup
    this.activeTimeouts = new Set();
    
    // Listen for scroll and pulse events from LiveView
    this.handleEvent("scroll-and-pulse", (params) => {
      // Check if this element is the target
      if (params.target && this.el.id === params.target) {
        this.scrollAndPulse(params)
      }
    })
  },

  // Proper cleanup to prevent memory leaks
  destroyed() {
    // Clear all active timeouts to prevent memory leaks
    this.activeTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.activeTimeouts.clear();
  },

  scrollAndPulse(params = {}) {
    const element = this.el
    const {
      duration = 2000,
      pulseColor = "success", 
      scrollBehavior = "smooth",
      scrollBlock = "center",
      offset = 0
    } = params

    // First, scroll the element into view
    if (offset && offset > 0) {
      // Custom scroll with offset - delay slightly to ensure it happens after any LiveView scrolling
      const scrollTimeout = setTimeout(() => {
        this.activeTimeouts.delete(scrollTimeout);

        // Get the element's position relative to the document (not viewport)
        let absoluteElementTop = 0;
        let currentElement = element;
        
        // Walk up the DOM tree to calculate absolute position
        while (currentElement) {
          absoluteElementTop += currentElement.offsetTop;
          currentElement = currentElement.offsetParent;
        }
        
        const targetPosition = Math.max(0, absoluteElementTop - offset);
        
        if (scrollBehavior === "instant") {
          window.scrollTo(0, targetPosition);
        } else {
          window.scrollTo({
            top: targetPosition,
            behavior: scrollBehavior
          });
        }
      }, 50); // Small delay to let LiveView finish any scrolling
      this.activeTimeouts.add(scrollTimeout);
    } else {
      // Default scrollIntoView behavior
      element.scrollIntoView({
        behavior: scrollBehavior,
        block: scrollBlock,
        inline: "nearest"
      })
    }

    // Apply pulse animation immediately (no delays for search results)
    if (offset && offset > 0) {
      // For search results with offset, apply animation immediately
      const timeout = setTimeout(() => {
        this.activeTimeouts.delete(timeout);
        this.applyPulseAnimation(element, duration, pulseColor)
      }, 100)
      this.activeTimeouts.add(timeout);
    } else {
      // Original behavior with delays for other use cases (like new blocks)
      const timeout1 = setTimeout(() => {
        this.activeTimeouts.delete(timeout1);
        
        // Scroll again in case the element wasn't visible before (e.g., closed accordion)
        element.scrollIntoView({
          behavior: scrollBehavior,
          block: scrollBlock,
          inline: "nearest"
        })
        
        // Then apply pulse animation after a short delay
        const timeout2 = setTimeout(() => {
          this.activeTimeouts.delete(timeout2);
          this.applyPulseAnimation(element, duration, pulseColor)
        }, 100)
        this.activeTimeouts.add(timeout2);
      }, 200)
      this.activeTimeouts.add(timeout1);
    }
  },

  applyPulseAnimation(element, duration, pulseColor) {
    // Add the pulse animation class with color
    element.classList.add('animate-pulse-highlight', `animate-pulse-${pulseColor}`)
    
    // Track timeout for cleanup
    const timeout = setTimeout(() => {
      this.activeTimeouts.delete(timeout);
      element.classList.remove('animate-pulse-highlight', `animate-pulse-${pulseColor}`)
    }, duration)
    this.activeTimeouts.add(timeout);
  },

  // Handle direct calls from LiveView push events
  updated() {
    // Check if this element was just added/updated and should pulse
    if (this.el.dataset.shouldPulse === "true") {
      this.scrollAndPulse()
      // Remove the flag to prevent repeated animations
      delete this.el.dataset.shouldPulse
    }
  }
}

export default ScrollPulseHook
